import dayjs from "dayjs";

const LOCK_NAME = "volca:token-refresh";
// Without Web Locks, each tab marks the refresh it has in flight under its
// own key, so a refused tab can tell another tab's refresh is still pending.
const PENDING_PREFIX = "volca:token-refresh:";
const TAB_ID = Math.random().toString(36).slice(2);
// A mark older than this is from a tab that closed mid-refresh.
const PENDING_MS = 30_000;
const POLL_MS = 100;

const otherPending = () =>
  Object.keys(localStorage).some((key) => {
    if (!key.startsWith(PENDING_PREFIX) || key === PENDING_PREFIX + TAB_ID)
      return false;
    const since = Number(localStorage.getItem(key));
    return dayjs().valueOf() - since < PENDING_MS;
  });

/**
 * Refreshes the session once for every tab of this browser.
 *
 * Each refresh rotates the user's token id on the server, which revokes the
 * refresh cookie the other tabs share and every access token issued before it.
 * Two tabs whose tokens expire together would both send the same cookie: the
 * second is refused, and logging out on that refusal ended the session the
 * first had just renewed. So tabs take turns (a Web Lock), and a tab whose
 * turn comes after another tab renewed the shared token uses that token
 * instead of refreshing again. The new token is stored before the lock is
 * released, so the next tab sees it. Without Web Locks the tabs cannot take
 * turns, so a refused tab waits while another tab's refresh is still in
 * flight, and uses the token it stores; it gives up only once no tab is
 * refreshing and none renewed the token.
 *
 * @param expired the token the caller found expired
 * @param refresh asks the server for a new token
 * @returns the token to use
 */
export const refreshAcrossTabs = async (
  expired: string | null,
  refresh: () => Promise<string>,
): Promise<string> => {
  const renewedElsewhere = () => {
    const current = localStorage.getItem("token");
    return current && current !== expired ? current : null;
  };
  const run = async () => {
    const shared = renewedElsewhere();
    if (shared) return shared;
    try {
      const token = await refresh();
      localStorage.setItem("token", token);
      return token;
    } catch (error) {
      const latest = renewedElsewhere();
      if (latest) return latest;
      throw error;
    }
  };

  const locks = typeof navigator === "undefined" ? undefined : navigator.locks;
  if (locks) return locks.request(LOCK_NAME, run);

  const mark = PENDING_PREFIX + TAB_ID;
  localStorage.setItem(mark, String(dayjs().valueOf()));
  try {
    return await run();
  } catch (error) {
    localStorage.removeItem(mark);
    while (otherPending()) {
      await new Promise((resolve) => setTimeout(resolve, POLL_MS));
      const latest = renewedElsewhere();
      if (latest) return latest;
    }
    const latest = renewedElsewhere();
    if (latest) return latest;
    throw error;
  } finally {
    localStorage.removeItem(mark);
  }
};
